import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Skeleton,
  IconButton,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from '@mui/material';
import {
  ShoppingCart,
  Receipt,
  Assignment,
  Person,
  Inventory2,
  ChevronLeft,
  ChevronRight,
  AddShoppingCart,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProducts } from '../hooks/useProducts';
import { useApiCart } from '../hooks/useApiCartProvider';
import { customerService, DashboardStats } from '../services/customerService';
import { promotionService, PromotionBanner } from '../services/promotionService';
import { orderService, Order, invoiceService } from '../services';
import type { InvoiceItem } from '../services/invoiceService';
import { Product } from '../types';
import { formatIDR } from '../utils/pricing';

// ─── Product Card ─────────────────────────────────────────────

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const hasDiscount = product.discount_percent && product.discount_percent > 0;
  const originalPrice = product.unit_price;
  const finalPrice = product.discounted_price || product.unitPrice || product.unit_price;

  const formatPrice = (price: number | null | undefined) => {
    if (!price) return '-';
    return `Rp ${price.toLocaleString('id-ID')}`;
  };

  return (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        minWidth: 200,
        maxWidth: 240,
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          borderColor: 'grey.300',
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => navigate('/products')}
    >
      <Box
        sx={{
          position: 'relative',
          height: 200,
          bgcolor: '#f8f9fa',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={product.image_url || '/product-placeholder.svg'}
          alt={product.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: 1,
            transition: 'transform 0.4s ease',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
          }}
        />
        {hasDiscount && (
          <Box sx={{
            position: 'absolute', top: 8, left: 8, bgcolor: '#ff424e', color: 'white',
            fontWeight: 700, fontSize: '0.75rem', px: 1, py: 0.3, borderRadius: 1, lineHeight: 1.3,
          }}>
            -{Number.isInteger(product.discount_percent!) ? product.discount_percent! : product.discount_percent!.toFixed(1)}%
          </Box>
        )}
        {/* Quick add overlay */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.5))', p: 1,
          display: 'flex', justifyContent: 'center',
          opacity: hovered ? 1 : 0, transition: 'opacity 0.25s ease',
        }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddShoppingCart sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
            sx={{ bgcolor: 'white', color: '#333', fontWeight: 600, fontSize: '0.7rem', textTransform: 'none', borderRadius: 5, px: 2, '&:hover': { bgcolor: '#f5f5f5' } }}
          >
            {t('dashboard.addToCart')}
          </Button>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 1.5, pb: '10px !important' }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8 }}>
          {product.family_name || ''}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            mt: 0.3, mb: 0.5, color: '#333',
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.4, minHeight: '2.8em',
          }}
        >
          {product.name}
        </Typography>
        {hasDiscount && originalPrice ? (
          <>
            <Typography variant="caption" sx={{ color: '#999', textDecoration: 'line-through', fontSize: '0.7rem', display: 'block', lineHeight: 1 }}>
              {formatPrice(originalPrice)}
            </Typography>
            <Typography sx={{ color: '#ee4d2d', fontWeight: 700, fontSize: '0.95rem' }}>
              {formatPrice(finalPrice)}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: '#222', fontWeight: 700, fontSize: '0.95rem' }}>
            {formatPrice(finalPrice)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Horizontal Carousel ──────────────────────────────────────

const HorizontalCarousel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 10);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', updateArrows, { passive: true });
    return () => { if (el) el.removeEventListener('scroll', updateArrows); };
  }, [updateArrows, children]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -el.clientWidth * 0.8 : el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {showLeft && (
        <IconButton onClick={() => scroll('left')} sx={{ position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)', zIndex: 2, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'grey.100' } }}>
          <ChevronLeft />
        </IconButton>
      )}
      <Box ref={scrollRef} sx={{ display: 'flex', gap: 2, overflowX: 'auto', scrollSnapType: 'x mandatory', pb: 1, px: 1, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
        {children}
      </Box>
      {showRight && (
        <IconButton onClick={() => scroll('right')} sx={{ position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)', zIndex: 2, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'grey.100' } }}>
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
};

// ─── Shop by Category Section ─────────────────────────────────

interface ProductSectionProps {
  title: string;
  tabs: string[];
  products: Product[];
  isLoading: boolean;
  activeTab: number;
  onTabChange: (index: number) => void;
  onAddToCart: (product: Product) => void;
}

const ProductSection: React.FC<ProductSectionProps> = ({ title, tabs, products, isLoading, activeTab, onTabChange, onAddToCart }) => {
  const { t } = useTranslation();
  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ whiteSpace: 'nowrap', mr: 2 }}>
          {title}
        </Typography>
        <Tabs
          value={activeTab}
          onChange={(_, v) => onTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'uppercase', fontWeight: 600, fontSize: '0.8rem', minWidth: 'auto', px: 2 } }}
        >
          {tabs.map((tab) => (
            <Tab key={tab} label={tab} />
          ))}
        </Tabs>
      </Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', gap: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" width={240} height={300} sx={{ borderRadius: 2, flexShrink: 0 }} />
          ))}
        </Box>
      ) : products.length > 0 ? (
        <HorizontalCarousel>
          {products.map((product) => (
            <Box key={product.id} sx={{ scrollSnapAlign: 'start' }}>
              <ProductCard product={product} onAddToCart={onAddToCart} />
            </Box>
          ))}
        </HorizontalCarousel>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('dashboard.noProductsFound')}
        </Typography>
      )}
    </Paper>
  );
};

// ─── Recent Orders Preview Panel ──────────────────────────────

const ORDER_STATUS_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'warning',
  SUBMITTED: 'primary',
  CONFIRMED: 'info',
  SHIPPED: 'secondary',
  DELIVERED: 'success',
  INVOICED: 'success',
  CLOSED: 'default',
  CANCELLED: 'error',
};

const formatShortDate = (iso: string, locale: string): string => {
  try {
    return new Date(iso).toLocaleDateString(locale === 'id' ? 'id-ID' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const RecentOrdersPanel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    orderService
      .getOrders({ limit: 5 })
      .then((res) => {
        if (!cancelled) setOrders(res || []);
      })
      .catch(() => {
        if (!cancelled) setOrders([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <Paper sx={{ p: 0, borderRadius: 2, overflow: 'hidden', height: '100%' }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={700}>
          {t('dashboard.recentOrdersHeading')}
        </Typography>
        <Button
          size="small"
          onClick={() => navigate('/orders')}
          sx={{ textTransform: 'none' }}
        >
          {t('dashboard.viewAll')}
        </Button>
      </Box>
      {loading ? (
        <Box sx={{ p: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : orders.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.noRecentOrders')}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                  {t('dashboard.tableOrderNumber')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                  {t('dashboard.tableDate')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                  {t('dashboard.tableTotal')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                  {t('dashboard.tableStatus')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => {
                const total = o.grand_total_amount ?? o.total_amount;
                return (
                  <TableRow
                    key={o.id}
                    hover
                    onClick={() => navigate(`/orders/${o.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {o.order_number || o.id.slice(0, 8)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {formatShortDate(o.order_date || o.created_at, i18n.language)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatIDR(total)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={o.status.charAt(0) + o.status.slice(1).toLowerCase()}
                        size="small"
                        color={ORDER_STATUS_COLOR[o.status] || 'default'}
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

// ─── Pending Invoices Preview Panel ───────────────────────────

const PendingInvoicesPanel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // POSTED == unpaid in this codebase. Sorted by due date so the panel
    // shows the five most recent due dates rather than whichever five rows
    // the invoice sync happened to write first.
    invoiceService
      .listInvoices(0, 5, 'POSTED', { sortBy: 'due_date', sortDir: 'desc' })
      .then((res) => {
        if (!cancelled) setInvoices(res?.invoices || []);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    return new Date(dueDate).getTime() < Date.now();
  };

  return (
    <Paper sx={{ p: 0, borderRadius: 2, overflow: 'hidden', height: '100%' }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={700}>
          {t('dashboard.pendingInvoicesHeading')}
        </Typography>
        <Button
          size="small"
          onClick={() => navigate('/invoices')}
          sx={{ textTransform: 'none' }}
        >
          {t('dashboard.viewAll')}
        </Button>
      </Box>
      {loading ? (
        <Box sx={{ p: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : invoices.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.noPendingInvoices')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2 }}>
          {invoices.map((inv) => {
            const overdue = isOverdue(inv.due_date);
            return (
              <Box
                key={inv.id}
                onClick={() => navigate(`/invoices/${inv.id}`)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    INV-{inv.invoice_number}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: overdue ? 'error.main' : 'text.secondary' }}
                  >
                    {t('dashboard.invoiceDue')}{' '}
                    {inv.due_date ? formatShortDate(inv.due_date, i18n.language) : '-'}
                    {overdue && ` (${t('dashboard.invoiceOverdue')})`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {formatIDR(inv.total_amount)}
                  </Typography>
                  <ChevronRightIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useApiCart();

  // Dashboard stats
  const [stats, setStats] = useState<DashboardStats>({ recent_orders: 0, pending_invoices: 0, account_balance: 0 });

  // Promotions
  const [promotions, setPromotions] = useState<PromotionBanner[]>([]);
  const [promoIndex, setPromoIndex] = useState(0);

  useEffect(() => {
    customerService.getDashboardStats()
      .then(setStats)
      .catch(() => {});
    promotionService.getActivePromotions()
      .then(setPromotions)
      .catch(() => {});
  }, []);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    if (promotions.length <= 1) return;
    const interval = setInterval(() => {
      setPromoIndex((prev) => (prev + 1) % promotions.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [promotions.length]);

  // Category (family) products
  const categoryHook = useProducts();
  const [categoryTab, setCategoryTab] = useState(0);
  const [categoryTabs, setCategoryTabs] = useState<string[]>([]);
  const [categoryFamilyCodes, setCategoryFamilyCodes] = useState<string[]>([]);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // Load initial products to discover families
  const initialHook = useProducts();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      initialHook.loadProducts({ limit: 100, offset: 0 });
      setInitialized(true);
    }
  }, [initialized, initialHook]);

  // Extract unique families from loaded products
  useEffect(() => {
    if (initialHook.products.length > 0) {
      const familyMap = new Map<string, string>();
      initialHook.products.forEach((p) => {
        if (p.family_code && p.family_name) {
          familyMap.set(p.family_code, p.family_name);
        }
      });
      const codes = Array.from(familyMap.keys());
      const names = Array.from(familyMap.values());
      if (codes.length > 0) {
        setCategoryFamilyCodes(codes);
        setCategoryTabs(names);
      }
    }
  }, [initialHook.products]);

  // Load products when tab changes
  useEffect(() => {
    if (categoryFamilyCodes.length > 0) {
      categoryHook.loadProducts({
        family_code: categoryFamilyCodes[categoryTab],
        limit: 20,
        offset: 0,
      });
    }
  }, [categoryTab, categoryFamilyCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddToCart = async (product: Product) => {
    try {
      await addToCart({ product_id: product.id, quantity: 1 });
      setSnackbar({ open: true, message: `${product.name} — ${t('dashboard.addToCart')}!`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' });
    }
  };

  const quickActions = [
    { title: t('dashboard.browseProducts'), icon: <ShoppingCart sx={{ fontSize: 32 }} />, color: '#e53935', href: '/products' },
    { title: t('dashboard.viewOrders'), icon: <Assignment sx={{ fontSize: 32 }} />, color: '#e53935', href: '/orders' },
    { title: t('dashboard.viewInvoices'), icon: <Receipt sx={{ fontSize: 32 }} />, color: '#e53935', href: '/invoices' },
    { title: t('dashboard.updateProfile'), icon: <Person sx={{ fontSize: 32 }} />, color: '#e53935', href: '/profile' },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      {/* Welcome Banner */}
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        {t('dashboard.welcomeGreeting', { name: user?.full_name || user?.name || '' })}
      </Typography>

      {/* Promotions Carousel */}
      {promotions.length > 0 && (
        <Box sx={{ mb: 3, position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: 180, sm: 260, md: 340 },
              bgcolor: 'grey.100',
            }}
          >
            {promotions.map((promo, idx) => (
              <Box
                key={promo.id}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: idx === promoIndex ? 1 : 0,
                  transition: 'opacity 0.6s ease-in-out',
                  pointerEvents: idx === promoIndex ? 'auto' : 'none',
                }}
              >
                <Box
                  component="img"
                  src={promo.image_url}
                  alt={promo.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {/* Overlay text */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    p: { xs: 2, md: 4 },
                    color: 'white',
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
                    {promo.title}
                  </Typography>
                  {promo.subtitle && (
                    <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                      {promo.subtitle}
                    </Typography>
                  )}
                  {promo.cta_text && promo.cta_url && (
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
                      onClick={() => {
                        if (promo.cta_url!.startsWith('http')) {
                          window.open(promo.cta_url!, '_blank', 'noopener,noreferrer');
                        } else {
                          navigate(promo.cta_url!);
                        }
                      }}
                    >
                      {promo.cta_text}
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Navigation arrows */}
          {promotions.length > 1 && (
            <>
              <IconButton
                onClick={() => setPromoIndex((prev) => (prev - 1 + promotions.length) % promotions.length)}
                sx={{
                  position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' },
                }}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() => setPromoIndex((prev) => (prev + 1) % promotions.length)}
                sx={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' },
                }}
                size="small"
              >
                <ChevronRight />
              </IconButton>

              {/* Dots indicator */}
              <Box
                sx={{
                  position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                  display: 'flex', gap: 0.75,
                }}
              >
                {promotions.map((_, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setPromoIndex(idx)}
                    sx={{
                      width: idx === promoIndex ? 24 : 8,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: idx === promoIndex ? 'white' : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Quick Actions */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {t('dashboard.quickActions')}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {quickActions.map((action, index) => (
          <Grid key={index} size={{ xs: 6, sm: 3 }}>
            <Card
              sx={{ textAlign: 'center', cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}
              onClick={() => navigate(action.href)}
            >
              <CardContent sx={{ py: 3 }}>
                <Box sx={{ color: action.color, mb: 1 }}>{action.icon}</Box>
                <Typography variant="body1" fontWeight={600}>{action.title}</Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2, pt: 0 }}>
                <Button variant="outlined" color="error" size="small" sx={{ textTransform: 'none' }}>
                  {t('common.select')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Summary Stats — clickable cards. Account Balance card is hidden
          for now (it currently sums unpaid invoice totals, which is unreliable
          for B2B customers paying through multiple channels). */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            onClick={() => navigate('/orders')}
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'box-shadow 0.2s, transform 0.2s',
              '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{t('dashboard.recentOrders')}</Typography>
              <Typography variant="h4" color="primary.main" fontWeight={700}>{stats.recent_orders}</Typography>
              <Typography variant="body2" color="text.secondary">{t('dashboard.ordersThisMonth')}</Typography>
            </Box>
            <Inventory2 sx={{ fontSize: 48, color: 'grey.200' }} />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            onClick={() => navigate('/invoices')}
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'box-shadow 0.2s, transform 0.2s',
              '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{t('dashboard.pendingInvoices')}</Typography>
              <Typography variant="h4" color="warning.main" fontWeight={700}>{stats.pending_invoices}</Typography>
              <Typography variant="body2" color="text.secondary">{t('dashboard.awaitingPayment')}</Typography>
            </Box>
            <Receipt sx={{ fontSize: 48, color: 'grey.200' }} />
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Orders + Upcoming Invoices preview panels */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentOrdersPanel />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PendingInvoicesPanel />
        </Grid>
      </Grid>

      {/* Shop by Category */}
      {categoryTabs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ProductSection
            title={t('dashboard.shopByCategory')}
            tabs={categoryTabs}
            products={categoryHook.products}
            isLoading={categoryHook.isLoading}
            activeTab={categoryTab}
            onTabChange={setCategoryTab}
            onAddToCart={handleAddToCart}
          />
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardPage;
